import React, { useEffect, useRef, useState } from "react";
import Axios from "axios";
import { connect } from "react-redux";
import { Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import Config from "../../../Config";
import {
  cartApi,
  get_promo,
  setClearCartState,
  setPromo,
} from "../../../../store/Actions/cartActions";
import {
  get_paymentId,
  setDescription,
  setEdit,
  setState,
} from "../../../../store/Actions/authActions";
import {
  buildOrderPayload,
  isPromoApplied,
  orderErrorMessage,
  orderTotals,
  splitAddress,
  toInternationalPhone,
} from "../../../../utils/checkout";
import { dateKey, parseSlot } from "../../../../utils/slotSelection";
import {
  FALLBACK_PRODUCT_ICON,
  localeKey,
  productName,
} from "../../../../utils/catalogue";
import {
  clearPendingWalletOrder,
  readPendingWalletOrder,
  savePendingWalletOrder,
} from "../../../../utils/walletOrder";
import WalletTopup, { formatEuro, shortfall } from "../../Modals/WalletTopup";
import ChangeAddress from "../../DeliveryDetail/changeAddressModal";
import { longDate } from "../Slots/format";
import CardForm from "./CardForm";
import { Icon, Sprite } from "./icons";
import switchAddress from "./switchAddress";
import "../Slots/slots.css";
import "./recap.css";

/**
 * Checkout, dernière étape — récapitulatif et paiement (design
 * Checkout.dc.html, écran 03). Remplace l'ancienne page /checkout pour une
 * nouvelle commande ; la modification d'une commande existante passe
 * encore par l'ancienne page (voir CheckoutEntry).
 *
 * Paiement :
 *  - Bancontact : la commande est réglée par le portefeuille WOSH. S'il ne
 *    couvre pas le total, on le recharge d'abord via Bancontact (pop-up 04)
 *    et, au retour de la banque (/wallet-success), la commande est confirmée
 *    d'elle-même dès que le crédit apparaît sur le portefeuille.
 *  - Carte : une carte enregistrée, ou une nouvelle saisie dans des champs
 *    Stripe Elements, enregistrée par POST /customer/payment_methods comme
 *    dans l'ancienne fenêtre « ajouter une carte ».
 *
 * La commande part avec les mêmes champs que l'ancien ConfirmOrder (voir
 * utils/checkout).
 */

const capitalise = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const todayKey = (now) => dateKey(now);

const Recap = ({
  history,
  dispatch,
  cartItems,
  subtotal,
  promo,
  address,
  homeAddresses,
  officeAddresses,
  isHome,
  pickup,
  pickupSlot,
  delivery,
  deliverySlot,
  cards,
  savedDescription,
  authUser,
  now,
}) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();

  const headers = {
    uid: authUser?.user_uid,
    client: authUser?.user_client,
    "access-token": authUser?.user_access_token,
  };

  const lines = cartItems || [];
  const savedCards = (cards || []).filter((card) => card?.id);

  /* Un panier vide à l'arrivée renvoie au catalogue ; vidé ici, on reste. */
  const [startedEmpty] = useState(lines.length === 0);

  const [addrOpen, setAddrOpen] = useState(false);
  const [changeTarget, setChangeTarget] = useState(null);
  const [description, setDescriptionText] = useState(savedDescription || "");

  /* Retour d'une recharge lancée ici : la commande attend d'être confirmée. */
  const [auto, setAuto] = useState(() =>
    history?.location?.state?.walletOrder ? readPendingWalletOrder() : null
  );
  const [walletChecks, setWalletChecks] = useState(0);
  const autoRun = useRef(null);

  const [balance, setBalance] = useState(null);
  const [phone, setPhone] = useState(undefined);
  const [phoneInput, setPhoneInput] = useState(auto?.phone || "");

  const [pay, setPay] = useState(auto ? "bancontact" : null);
  const [cardChoice, setCardChoice] = useState(
    savedCards[0]?.id || "new"
  );
  const [holder, setHolder] = useState("");
  const [cardFields, setCardFields] = useState({});
  const [cardError, setCardError] = useState(null);

  const [promoInput, setPromoInput] = useState("");
  const [promoError, setPromoError] = useState(false);
  const [promoLoading, setPromoLoading] = useState(false);

  const [topupOpen, setTopupOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const loadWallet = () =>
    Axios({ url: `${Config.apiURL}/customer/wallet`, method: "GET", headers })
      .then((response) => setBalance(Number(response?.data?.wallet?.amount) || 0))
      .catch(() => setBalance((current) => (current === null ? 0 : current)));

  useEffect(() => {
    loadWallet();

    Axios({ url: `${Config.apiURL}/customer/profiles`, method: "GET", headers })
      .then((response) => setPhone(response?.data?.profile?.phone_number || null))
      .catch(() => setPhone(null));

    dispatch(get_paymentId());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* La liste des cartes arrive après le premier rendu. */
  useEffect(() => {
    if (cardChoice === "new" && savedCards.length) {
      setCardChoice(savedCards[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedCards.length]);

  useEffect(() => {
    autoRun.current?.();
  }, [auto, balance, phone, walletChecks]);

  if (startedEmpty || !address) {
    return <Redirect to="/home" />;
  }

  const pickupKey = dateKey(pickup?.date?.date);
  const deliveryKey = dateKey(delivery?.date?.date);

  if (!pickupKey || !pickupSlot) {
    return <Redirect to="/checkout/pickup" />;
  }

  if (!deliveryKey || !deliverySlot) {
    return <Redirect to="/checkout/delivery" />;
  }

  const count = lines.reduce((sum, line) => sum + (line.quantity || 0), 0);
  const countLabel =
    count === 1 ? t("1 item") : t("{{count}} items", { count });

  const totals = orderTotals({ subtotal, promo, address, isHome });
  const promoOn = isPromoApplied(promo);
  const needed = balance === null ? 0 : shortfall(totals.total, balance);

  const isCollectionPoint = address?.type === "CollectionPoint";
  const { line1, line2 } = splitAddress(address?.address);
  const pointName = address?.companies?.[0]?.company_name;

  const addressRows = [
    ...(homeAddresses || []).map((item) => ({
      item,
      emoji: "🏠",
      label: t("Home (address)"),
    })),
    ...(officeAddresses || []).map((item) => ({
      item,
      emoji: "🏢",
      label: item?.companies?.[0]?.company_name || t("Collection point"),
    })),
  ];

  const needsPhone = phone === null;
  const cardComplete =
    cardChoice !== "new" ||
    (cardFields.number && cardFields.expiry && cardFields.cvc && holder.trim());

  const blocker =
    count === 0
      ? t("Add at least one item")
      : !pay
      ? t("Choose a payment method to continue")
      : needsPhone && !phoneInput.trim()
      ? t("Add a phone number so the driver can reach you")
      : pay === "card" && !cardComplete
      ? t("Complete your card details")
      : null;

  const selectedCard = savedCards.find((card) => card.id === cardChoice);

  const ctaHint =
    blocker ||
    (pay === "bancontact"
      ? needed > 0
        ? t("Your wallet does not cover this order: you will top it up first.")
        : `${t("Paid with your WOSH wallet")} · ${t("Balance")} ${formatEuro(balance)}`
      : selectedCard
      ? `${t("Paid by card")} •••• ${selectedCard.last4}`
      : t("Paid by card"));

  const pickupRange = parseSlot(pickupSlot)?.label || pickupSlot;
  const deliveryRange = parseSlot(deliverySlot)?.label || deliverySlot;

  /* ── Actions ─────────────────────────────────────────────── */

  const applyPromo = async () => {
    const code = promoInput.trim();

    if (!code || promoLoading) {
      return;
    }

    setPromoLoading(true);
    const ok = await dispatch(setPromo(code));
    setPromoLoading(false);

    if (!ok) {
      /* setPromo range aussi la réponse d'erreur dans cart.promo. */
      dispatch(get_promo(null));
      setPromoError(true);
    }
  };

  const removePromo = () => {
    dispatch(get_promo(null));
    setPromoInput("");
    setPromoError(false);
  };

  const saveNewCard = async () => {
    const { token, error: stripeError } = await stripe.createToken(
      elements.getElement(CardNumberElement),
      { name: holder.trim() }
    );

    if (stripeError) {
      setCardError(stripeError.message);
      return null;
    }

    try {
      const response = await Axios({
        url: `${Config.apiURL}/customer/payment_methods`,
        method: "POST",
        headers,
        data: {
          token: token.id,
          laundry_place_id: JSON.parse(localStorage.getItem("laundry_id")),
        },
      });
      const id = response?.data?.payment_method?.id;

      dispatch(get_paymentId());
      setCardChoice(id);
      return id;
    } catch (err) {
      const data = err?.response?.data;

      setError(
        (data?.message?.payment_method &&
          [].concat(data.message.payment_method).join(" ")) ||
          orderErrorMessage(data) ||
          t("Something went wrong, try again later")
      );
      return null;
    }
  };

  const confirm = async () => {
    setError(null);
    setCardError(null);

    if (pickupKey < todayKey(now || new Date())) {
      setError(t("This pickup slot has passed. Please choose another one."));
      return;
    }

    if (pay === "bancontact" && needed > 0) {
      /* Au retour de la banque, /wallet-success renvoie ici pour confirmer. */
      savePendingWalletOrder({ phone: phoneInput });
      setTopupOpen(true);
      return;
    }

    setSubmitting(true);

    if (needsPhone) {
      try {
        await Axios({
          url: `${Config.authURL}/auth`,
          method: "PUT",
          headers,
          data: { phone_number: toInternationalPhone(phoneInput) },
        });
      } catch (err) {
        setError(
          orderErrorMessage(err?.response?.data) ||
            t("Something went wrong, try again later")
        );
        setSubmitting(false);
        return;
      }
    }

    let payment = { type: "Wallet" };

    if (pay === "card") {
      const cardId = cardChoice === "new" ? await saveNewCard() : cardChoice;

      if (!cardId) {
        setSubmitting(false);
        return;
      }

      payment = { type: "Card", cardId };
    }

    let created;
    try {
      created = await Axios({
        url: `${Config.apiURL}/customer/orders`,
        method: "POST",
        headers,
        data: buildOrderPayload({
          pickup,
          pickupSlot,
          delivery,
          deliverySlot,
          address,
          isHome,
          promo,
          cartItems: lines,
          description,
          payment,
        }),
      });
    } catch (err) {
      setError(
        orderErrorMessage(err?.response?.data) ||
          t("Something went wrong, try again later")
      );
      setSubmitting(false);
      return;
    }

    localStorage.removeItem("p_id");
    localStorage.removeItem("tM");
    /* On quitte la page avant de vider le panier, sinon l'écran vide
       apparaîtrait un instant. */
    /* L'écran de confirmation (07) affiche le numéro et les deux créneaux. */
    const order = created?.data?.order || created?.data;
    history.push("/purchase-success", {
      orderNumber: order?.wosh_order_id || order?.id || null,
      pickup: { key: pickupKey, slot: pickupSlot },
      delivery: { key: deliveryKey, slot: deliverySlot },
    });
    dispatch(setEdit(false));
    dispatch(setState());
    dispatch(setClearCartState());
  };

  /*
   * Retour de recharge : le crédit est fait côté serveur, parfois quelques
   * secondes après le paiement. On relit le solde jusqu'à ce qu'il couvre
   * la commande, puis on confirme comme si l'utilisateur avait cliqué.
   */
  autoRun.current = () => {
    if (!auto || submitting || balance === null || phone === undefined) {
      return;
    }

    if (needed > 0) {
      if (walletChecks >= 8) {
        setAuto(null);
        clearPendingWalletOrder();
        setError(
          t("Your top-up is still being processed. Confirm the order again in a moment.")
        );
        return;
      }

      setTimeout(() => {
        loadWallet().then(() => setWalletChecks((n) => n + 1));
      }, 1500);
      return;
    }

    setAuto(null);
    clearPendingWalletOrder();

    if (!blocker) {
      confirm();
    }
  };

  /* ── Rendu ───────────────────────────────────────────────── */

  return (
    <div className="wosh-slot wosh-rc">
      <Sprite />

      <div className="wosh-slot__top">
        <button
          type="button"
          className="wosh-slot__back"
          aria-label={t("Back")}
          onClick={() => history.push("/checkout/delivery")}
        >
          <Icon name="arrow-back" />
        </button>

        <div className="wosh-slot__progress">
          <div className="wosh-slot__segments" aria-hidden="true">
            <span className="is-done" />
            <span className="is-done" />
            <span className="is-done" />
          </div>
          <div className="wosh-slot__step">{t("Summary")}</div>
        </div>
      </div>

      <div className="wosh-slot__head">
        <h1 className="wosh-slot__title">{t("Check your order")}</h1>
        <p className="wosh-slot__lede">
          {t("Everything can be changed before you confirm.")}
        </p>
      </div>

      <div className="wosh-slot__body">
        <main>
          {/* Passages */}
          <section className="wosh-rc__card">
            <div className="wosh-rc__card-head">
              <div className="wosh-rc__eyebrow">{t("Visits")}</div>
              <button
                type="button"
                className="wosh-rc__link"
                onClick={() => history.push("/checkout/pickup")}
              >
                {t("Change the slots")}
              </button>
            </div>

            <div className="wosh-rc__passes">
              <div className="wosh-rc__pass">
                <span className="wosh-rc__badge">
                  <Icon name="cube" />
                </span>
                <div>
                  <div className="wosh-rc__pass-label">{t("Pickup")}</div>
                  <div className="wosh-rc__pass-date">
                    {capitalise(longDate(pickupKey))}
                  </div>
                  <div className="wosh-rc__pass-range">{pickupRange}</div>
                </div>
              </div>

              <div className="wosh-rc__pass">
                <span className="wosh-rc__badge">
                  <Icon name="truck" />
                </span>
                <div>
                  <div className="wosh-rc__pass-label">{t("Delivery")}</div>
                  <div className="wosh-rc__pass-date">
                    {capitalise(longDate(deliveryKey))}
                  </div>
                  <div className="wosh-rc__pass-range">{deliveryRange}</div>
                </div>
              </div>
            </div>

            <div className="wosh-rc__note">
              {t("Can be changed until 1 hour before pickup.")}
            </div>
          </section>

          {/* Adresse */}
          <section className="wosh-rc__card">
            <div className="wosh-rc__card-head">
              <div className="wosh-rc__eyebrow">{t("Address")}</div>
              <button
                type="button"
                className="wosh-rc__link"
                aria-expanded={addrOpen}
                onClick={() => setAddrOpen(!addrOpen)}
              >
                {addrOpen ? t("Done") : t("Edit")}
              </button>
            </div>

            {!addrOpen ? (
              <>
                <div className="wosh-rc__addr">
                  <span className="wosh-rc__badge">
                    <Icon name="location" />
                  </span>
                  <div className="wosh-rc__addr-body">
                    <div className="wosh-rc__addr-line1">
                      {isCollectionPoint && pointName ? pointName : line1}
                    </div>
                    <div className="wosh-rc__addr-line2">
                      {isCollectionPoint && pointName ? address?.address : line2}
                    </div>
                    <div className="wosh-rc__addr-note">
                      {isCollectionPoint
                        ? t("Pickup and delivery at this collection point")
                        : t("Pickup and delivery at this address")}
                    </div>
                  </div>
                </div>

                <div className="wosh-rc__extra">
                  <label className="wosh-rc__label" htmlFor="wosh-rc-notes">
                    {t("Instructions for the driver")}{" "}
                    <span className="wosh-rc__optional">({t("optional")})</span>
                  </label>
                  <textarea
                    id="wosh-rc-notes"
                    className="wosh-rc__textarea"
                    rows={2}
                    maxLength={200}
                    value={description}
                    placeholder={t("Door code, floor, where to leave the bag…")}
                    onChange={(event) => {
                      setDescriptionText(event.target.value);
                      dispatch(setDescription(event.target.value));
                    }}
                  />

                  {needsPhone && (
                    <div className="wosh-rc__phone">
                      <label className="wosh-rc__label" htmlFor="wosh-rc-phone">
                        {t("Phone number")}
                      </label>
                      <div className="wosh-rc__input wosh-rc__input--soft">
                        <span className="wosh-rc__prefix">+32</span>
                        <input
                          id="wosh-rc-phone"
                          type="tel"
                          autoComplete="tel-national"
                          value={phoneInput}
                          placeholder="470 12 34 56"
                          onChange={(event) => setPhoneInput(event.target.value)}
                        />
                      </div>
                      <div className="wosh-rc__help">
                        {t("So the driver can reach you on the day.")}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="wosh-rc__addr-list" role="radiogroup">
                {addressRows.map(({ item, emoji, label }) => {
                  const on = item.id === address?.id;
                  const rows = splitAddress(item.address);

                  return (
                    <button
                      type="button"
                      role="radio"
                      aria-checked={on}
                      key={`${emoji}-${item.id}`}
                      className={`wosh-rc__addr-row${on ? " wosh-rc__addr-row--on" : ""}`}
                      onClick={() => {
                        if (on) {
                          setAddrOpen(false);
                        } else {
                          setChangeTarget(item);
                        }
                      }}
                    >
                      <span className="wosh-rc__emoji" aria-hidden="true">
                        {emoji}
                      </span>
                      <span className="wosh-rc__addr-row-body">
                        <span className="wosh-rc__addr-row-title">
                          {label} · {rows.line1}
                        </span>
                        <span className="wosh-rc__addr-row-line">{rows.line2}</span>
                      </span>
                      <span className="wosh-rc__mark">
                        <Icon name="check" />
                      </span>
                    </button>
                  );
                })}

                <button
                  type="button"
                  className="wosh-rc__add-addr"
                  onClick={() => history.push("/use-wosh")}
                >
                  + {t("Add an address")}
                </button>
              </div>
            )}
          </section>

          {/* Commande */}
          <section className="wosh-rc__card">
            <div className="wosh-rc__card-head wosh-rc__card-head--tight">
              <div className="wosh-rc__eyebrow">
                {t("Your order")} · {countLabel}
              </div>
              <button
                type="button"
                className="wosh-rc__link"
                onClick={() => history.push("/home")}
              >
                + {t("Add items")}
              </button>
            </div>

            {lines.map((line) => (
              <div className="wosh-rc__line" key={line.id}>
                <span className="wosh-rc__line-icon">
                  <img src={line.icon || FALLBACK_PRODUCT_ICON} alt="" />
                </span>
                <div className="wosh-rc__line-body">
                  <div className="wosh-rc__line-name">
                    {productName(line, localeKey())}
                  </div>
                  <div className="wosh-rc__line-unit">
                    {formatEuro(line.price / (line.quantity || 1))} / {t("piece")}
                  </div>
                </div>
                <div className="wosh-rc__stepper">
                  <button
                    type="button"
                    aria-label={t("Remove one")}
                    onClick={() => dispatch(cartApi(line, "minus"))}
                  >
                    −
                  </button>
                  <span className="wosh-rc__qty">{line.quantity}</span>
                  <button
                    type="button"
                    aria-label={t("Add one")}
                    onClick={() => dispatch(cartApi(line, "plus"))}
                  >
                    +
                  </button>
                </div>
                <div className="wosh-rc__line-sum">{formatEuro(line.price)}</div>
                <button
                  type="button"
                  className="wosh-rc__remove"
                  title={t("Remove")}
                  aria-label={t("Remove")}
                  onClick={() => dispatch(cartApi(line, "destroyAllProducts"))}
                >
                  <Icon name="close" />
                </button>
              </div>
            ))}

            {!lines.length && (
              <div className="wosh-rc__empty">{t("Your cart is empty.")}</div>
            )}

            <div className="wosh-rc__hint">
              <Icon name="info" />
              <span>
                {t(
                  "Bag weight is checked at the workshop. The final amount is adjusted if needed."
                )}
              </span>
            </div>
          </section>

          {/* Paiement */}
          <section className="wosh-rc__card">
            <div className="wosh-rc__card-head">
              <div className="wosh-rc__eyebrow">{t("Payment method")}</div>
              <div className="wosh-rc__secure">
                <Icon name="lock" />
                {t("Secure payment")}
              </div>
            </div>

            <div className="wosh-rc__pays" role="radiogroup">
              <div className={`wosh-rc__pay${pay === "bancontact" ? " wosh-rc__pay--on" : ""}`}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={pay === "bancontact"}
                  className="wosh-rc__pay-head"
                  onClick={() => setPay("bancontact")}
                >
                  <span className="wosh-rc__radio" />
                  <span className="wosh-rc__pay-body">
                    <span className="wosh-rc__pay-label">Bancontact</span>
                    <span className="wosh-rc__pay-meta">
                      {t("Via your banking app")}
                      {balance !== null &&
                        ` · ${t("Wallet")} ${formatEuro(balance)}`}
                    </span>
                  </span>
                  <span className="wosh-rc__logo">BC</span>
                </button>
              </div>

              <div className={`wosh-rc__pay${pay === "card" ? " wosh-rc__pay--on" : ""}`}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={pay === "card"}
                  className="wosh-rc__pay-head"
                  onClick={() => setPay("card")}
                >
                  <span className="wosh-rc__radio" />
                  <span className="wosh-rc__pay-body">
                    <span className="wosh-rc__pay-label">{t("Bank card")}</span>
                    <span className="wosh-rc__pay-meta">Visa, Mastercard</span>
                  </span>
                  <span className="wosh-rc__logo">VISA</span>
                </button>

                {pay === "card" && (
                  <div className="wosh-rc__pay-extra">
                    {savedCards.length > 0 && (
                      <div className="wosh-rc__cards" role="radiogroup">
                        {savedCards.map((card) => (
                          <button
                            type="button"
                            role="radio"
                            aria-checked={cardChoice === card.id}
                            key={card.id}
                            className={`wosh-rc__saved${
                              cardChoice === card.id ? " wosh-rc__saved--on" : ""
                            }`}
                            onClick={() => setCardChoice(card.id)}
                          >
                            <Icon name="card" />
                            <span>
                              {card.brand ? `${capitalise(card.brand)} ` : ""}
                              •••• {card.last4}
                            </span>
                          </button>
                        ))}
                        <button
                          type="button"
                          role="radio"
                          aria-checked={cardChoice === "new"}
                          className={`wosh-rc__saved${
                            cardChoice === "new" ? " wosh-rc__saved--on" : ""
                          }`}
                          onClick={() => setCardChoice("new")}
                        >
                          <span className="wosh-rc__plus">+</span>
                          <span>{t("New card")}</span>
                        </button>
                      </div>
                    )}

                    {cardChoice === "new" && (
                      <CardForm
                        t={t}
                        holder={holder}
                        onHolder={setHolder}
                        error={cardError}
                        onFieldChange={(field, event) => {
                          setCardError(event.error ? event.error.message : null);
                          setCardFields((fields) => ({
                            ...fields,
                            [field]: event.complete,
                          }));
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>

        <aside className="wosh-rc__aside">
          <div className="wosh-rc__promo">
            <div className="wosh-rc__eyebrow wosh-rc__eyebrow--gap">
              {t("Promo code")}
            </div>

            {!promoOn ? (
              <>
                <div
                  className={`wosh-rc__promo-field${
                    promoError ? " wosh-rc__promo-field--error" : ""
                  }`}
                >
                  <Icon name="tag" />
                  <input
                    type="text"
                    value={promoInput}
                    aria-label={t("Promo code")}
                    placeholder={t("Enter a code")}
                    onChange={(event) => {
                      setPromoInput(event.target.value);
                      setPromoError(false);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        applyPromo();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="wosh-rc__apply"
                    disabled={promoLoading}
                    onClick={applyPromo}
                  >
                    {promoLoading ? "…" : t("Apply")}
                  </button>
                </div>
                {promoError && (
                  <div className="wosh-rc__field-error" role="alert">
                    {t("This code is not valid.")}
                  </div>
                )}
              </>
            ) : (
              <div className="wosh-rc__promo-on">
                <Icon name="tag" />
                <div className="wosh-rc__promo-on-body">
                  <div className="wosh-rc__promo-code">
                    {String(promo.code || promo.name || promoInput).toUpperCase()}
                  </div>
                  <div className="wosh-rc__promo-sub">
                    {promo.fixed_amount
                      ? `−${formatEuro(promo.discount)}`
                      : `−${promo.discount} % ${t("on items")}`}
                  </div>
                </div>
                <button
                  type="button"
                  className="wosh-rc__promo-remove"
                  title={t("Remove the code")}
                  aria-label={t("Remove the code")}
                  onClick={removePromo}
                >
                  <Icon name="close" />
                </button>
              </div>
            )}
          </div>

          <div className="wosh-rc__summary">
            <div className="wosh-rc__eyebrow wosh-rc__eyebrow--gap">
              {t("Order summary")}
            </div>

            <div className="wosh-rc__row">
              <span>
                {t("Items")} ({countLabel})
              </span>
              <span className="wosh-rc__row-value">
                {formatEuro(totals.subtotal)}
              </span>
            </div>

            {promoOn && totals.discount > 0 && (
              <div className="wosh-rc__row">
                <span>
                  {t("Code")}{" "}
                  {String(promo.code || promo.name || promoInput).toUpperCase()}
                </span>
                <span className="wosh-rc__row-value wosh-rc__row-value--mint">
                  −{formatEuro(totals.discount)}
                </span>
              </div>
            )}

            {totals.fees.length === 0 ? (
              <div className="wosh-rc__row wosh-rc__row--last">
                <span>{t("Pickup & delivery")}</span>
                <span className="wosh-rc__row-value wosh-rc__row-value--mint">
                  {t("Free")}
                </span>
              </div>
            ) : (
              totals.fees.map((fee, index) => (
                <div
                  key={fee.kind}
                  className={`wosh-rc__row${
                    index === totals.fees.length - 1 ? " wosh-rc__row--last" : ""
                  }`}
                >
                  <span>
                    {fee.kind === "service" ? t("Service fee") : t("Delivery fee")}
                  </span>
                  <span className="wosh-rc__row-value">{formatEuro(fee.amount)}</span>
                </div>
              ))
            )}

            <div className="wosh-rc__total">
              <span>{t("Total")}</span>
              <span className="wosh-rc__total-value">{formatEuro(totals.total)}</span>
            </div>
            <div className="wosh-rc__vat">{t("VAT included")}</div>

            {error && (
              <p className="wosh-slot__error" role="alert">
                {error}
              </p>
            )}

            <button
              type="button"
              className="wosh-btn"
              disabled={Boolean(blocker) || submitting || Boolean(auto)}
              onClick={confirm}
            >
              {submitting || auto ? t("processing") : t("Confirm the order")}
            </button>
            <div className="wosh-rc__cta-hint">{ctaHint}</div>
          </div>
        </aside>
      </div>

      <WalletTopup
        open={topupOpen}
        onClose={() => {
          clearPendingWalletOrder();
          setTopupOpen(false);
        }}
        total={totals.total}
        balance={balance || 0}
      />

      <ChangeAddress
        open={Boolean(changeTarget)}
        onClose={() => setChangeTarget(null)}
        handleSubmit={() => dispatch(switchAddress(changeTarget))}
      />
    </div>
  );
};

export { Recap };

const mapStateToProps = (state) => ({
  cartItems: state?.cart?.cartItems,
  subtotal: state?.cart?.totalAmount,
  promo: state?.cart?.promo,
  address: state?.auth?.selectedAddress?.[0],
  homeAddresses: state?.auth?.homeAddress,
  officeAddresses: state?.auth?.officeAddress,
  /* Même lecture que les écrans de créneaux. */
  isHome: state?.cart?.home === true,
  pickup: state?.cart?.pickup,
  pickupSlot: state?.auth?.pickupTime,
  delivery: state?.cart?.time,
  deliverySlot: state?.auth?.deliveryTime,
  cards: state?.auth?.paymentId,
  savedDescription: state?.auth?.desc,
  authUser: state?.auth?.user,
});

export default connect(mapStateToProps)(Recap);
